import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { CATEGORIES, MARKETS } from "@/lib/constants";
import api from "@/lib/api";
import * as Icons from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [market, setMarket] = useState("Global");
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/reports/recent").then((r) => setRecent(r.data.reports || [])).catch(() => {});
  }, []);

  const go = (cat) => {
    const q = market !== "Global" ? `?market=${market}` : "";
    nav(cat.path + q);
  };

  return (
    <main className="mx-auto max-w-7xl px-6 py-10 md:px-8 lg:py-14">
      <div className="animate-in-up">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500">Dashboard</p>
        <h1 className="mt-2 font-head text-4xl font-medium tracking-tight text-stone-900 sm:text-5xl">
          Hi {user?.name?.split(" ")[0] || "there"} — what would you like to research?
        </h1>
        <p className="mt-3 max-w-2xl text-base text-stone-600">
          Pick a category and a market. We detect the exchange, country and currency automatically, then explain everything in plain English.
        </p>
      </div>

      {/* Market filter */}
      <div className="mt-8">
        <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-stone-500">Global market filter</p>
        <div className="flex flex-wrap gap-2" data-testid="market-filter">
          {MARKETS.map((m) => (
            <button key={m.id} data-testid={`market-${m.id}`} onClick={() => setMarket(m.id)}
              className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-all duration-200 ${
                market === m.id ? "border-navy bg-navy text-white" : "border-stone-200 bg-white text-stone-700 hover:bg-stone-50"
              }`}>
              <span>{m.flag}</span>{m.label}
            </button>
          ))}
        </div>
      </div>

      {/* Category selector */}
      <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {CATEGORIES.map((c, i) => {
          const Icon = Icons[c.icon] || Icons.Circle;
          return (
            <button key={c.id} data-testid={`category-${c.id}`} onClick={() => go(c)}
              className="group flex flex-col items-start rounded-xl border border-stone-200 bg-white p-6 text-left transition-all duration-200 hover:border-navy hover:shadow-sm animate-in-up"
              style={{ animationDelay: `${i * 40}ms` }}>
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-emerald-50 text-navy transition-colors group-hover:bg-navy group-hover:text-white">
                <Icon size={20} strokeWidth={1.5} />
              </span>
              <h3 className="mt-4 font-head text-xl font-medium text-stone-900">{c.label}</h3>
              <p className="mt-1 text-sm text-stone-500">{c.desc}</p>
            </button>
          );
        })}
      </div>

      {recent.length > 0 && (
        <div className="mt-12">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-stone-500">Recent research</p>
          <div className="divide-y divide-stone-200 rounded-xl border border-stone-200 bg-white">
            {recent.slice(0, 6).map((r, i) => (
              <div key={r._report_id || i} className="flex items-center justify-between px-5 py-3.5" data-testid={`recent-${i}`}>
                <div className="flex items-center gap-3">
                  <span className="rounded-full bg-stone-100 px-2 py-0.5 text-xs font-medium text-stone-600">{r.type}</span>
                  <span className="font-medium text-stone-800">{r.name || r.ai?.fund_name || r.ticker || "Report"}</span>
                </div>
                <span className="tabular text-xs text-stone-400">{(r.created_at || "").slice(0, 10)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </main>
  );
}
